import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
  of,
  ReplaySubject,
  Subject,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { OrderformFascade } from '../../../fascades/orderform.fascade';
import { HttpClient } from '@angular/common/http';
import { InterfacePlaceDetail, InterfacePlacesList } from './interface';
import { generateUUID } from '@ds24/ds-generic-api';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

export const ADDRESS_INPUT_DELAY = 250;
export const ADDRESS_SUGGEST_SESSION_BASED = false;

@Component({
  selector: 'orderform-address-suggest-plugin',
  templateUrl: './address-suggest-plugin.component.html',
  styleUrls: ['./address-suggest-plugin.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressSuggestPluginComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public startGoogleSuggestOnChars = 2;
  public input$ = new ReplaySubject<AbstractControl>(null);
  private _destroy$ = new Subject<void>();
  private _defaultAutocompleteValueOfStreet: string;

  private _sessionToken = generateUUID();

  /**
   * ID of the input
   */
  @Input() inputId: string;

  @Input() formGroupRef: UntypedFormGroup;

  private _sessionBased = false;
  private _disableSuggestWhileSuggestSelection = false;
  /**
   * Hier wird die Sessionbasierte Abrechnung mit Google
   * aktiviert.
   * @param v
   */
  @Input() set sessionBased(v: any) {
    this._sessionBased = coerceBooleanProperty(v);
  }

  @Input() set inputControl(input: AbstractControl) {
    if (input) {
      this.input$.next(input);
    }
  }

  private _isInputAutoFilled = false;

  inputValue$ = this.input$.pipe(
    switchMap((input) => input.valueChanges.pipe(startWith(input.value))),
    tap(() => this.checkValueToDisableBrowserSuggest(this._getInputElement())),
    shareReplay()
  );

  focus$ = new BehaviorSubject(false);

  show$ = combineLatest([this.inputValue$, this.focus$]).pipe(
    debounceTime(ADDRESS_INPUT_DELAY), // need debounce to click an suggest item
    map(([value, focus]) => {
      return value?.length > this.startGoogleSuggestOnChars && focus;
    })
  );

  /**
   * !!!! Attention
   * please the comments first before change the code!!
   * The logic of "browser suggest" and "google suggest" works directly opposite!
   */
  suggests$ = this.inputValue$.pipe(
    distinctUntilChanged(),
    debounceTime(ADDRESS_INPUT_DELAY),
    withLatestFrom(this._orderFormFascade.settings$),
    takeUntil(this._destroy$),
    filter(([inputValue, settings]) => {
      if (!inputValue) {
        return false;
      }

      if (settings.config.disable_address_autocomplete === true) {
        return false;
      }

      if (this._isInputAutoFilled) {
        return false;
      }

      if (this._disableSuggestWhileSuggestSelection) {
        return false;
      }

      if (window.location.search.indexOf('test_skip_autocomplete=1') >= 0) {
        return false;
      }

      /**
       * If google suggest activate
       */
      return inputValue.length > this.startGoogleSuggestOnChars;
    }),
    switchMap(([inputValue, settings]) =>
      this.getSuggestsList$(
        inputValue,
        settings.global.language,
        this._sessionToken
      )
    ),
    filter((response) => !!response)
  );

  constructor(
    private _orderFormFascade: OrderformFascade,
    private _httpClient: HttpClient,
    private _ngZone: NgZone
  ) {}

  checkValueToDisableBrowserSuggest(input: HTMLInputElement) {
    if (input.value.length > this.startGoogleSuggestOnChars) {
      /**
       * Need handle browser suggest in seperated if because
       * "add browser suggest" shows direct the suggestion
       * "remove browser suggest", removes only suggestion after change the input
       */
      this.removeBrowserSuggest(input);
    } else {
      this.addBrowserSuggest(input);
    }
  }

  ngOnInit(): void {}

  private _getInputElement() {
    const input: HTMLInputElement = document.getElementById(
      this.inputId
    ) as HTMLInputElement;
    return input;
  }

  ngAfterViewInit() {
    this._ngZone.runOutsideAngular(() =>
      setTimeout(() => {
        const input = this._getInputElement();
        if (input) {
          this._ngZone.run(() => {
            input.onfocus = () => {
              this.focus$.next(true);
            };
            input.onblur = () => {
              this.focus$.next(false);
            };

            input.classList.add('autofill-events');
            input.addEventListener('animationstart', (e) => {
              switch (e.animationName) {
                case 'onAutoFillStart':
                  this._isInputAutoFilled = true;
                  break;
                case 'onAutoFillCancel':
                  this._isInputAutoFilled = false;
                  break;
              }
            });
          });

          this.backupAutocompleteValue(input);
          this.checkValueToDisableBrowserSuggest(input);
        }
      })
    );
  }

  /**
   * Get list of places
   */
  private getSuggestsList$(
    input: string,
    language: string,
    sessionToken: string
  ): Observable<InterfacePlacesList.Prediction[]> {
    return this._httpClient
      .get(
        `/v2/api/public/autocomplete?input=${input}&language=${language}&types=address${
          this._sessionBased ? '&sessiontoken=' + sessionToken : ''
        }`
      )
      .pipe(
        map((result: InterfacePlacesList.RootObject) => {
          return result?.predictions;
        }),
        catchError((e) => {
          return of([]);
        })
      );
  }

  /**
   * Get details for a place (street, street_number, etc)
   */
  private getSuggestDetails$(placeID: string, sessionToken: string) {
    return this._httpClient
      .get(
        `/v2/api/public/autocomplete/place?place_id=${placeID}&fields=address_components${
          this._sessionBased ? '&sessiontoken=' + sessionToken : ''
        }`
      )
      .pipe(
        map((result: InterfacePlaceDetail.RootObject) => {
          return result.result;
        })
      );
  }

  /**
   * Backup init input autocomplete value
   */
  backupAutocompleteValue(input: HTMLInputElement) {
    if (input) {
      const value = input.getAttribute('autocomplete');
      this._defaultAutocompleteValueOfStreet = value;
    }
  }

  /**
   * remove browser suggestion by change the autocomplete attr.
   */
  removeBrowserSuggest(input: HTMLInputElement) {
    if (input) {
      input.setAttribute('autocomplete', 'new-password');
    }
  }

  /**
   * restore browser suggestion by adding previews autocomplete attr. (if existed)
   */
  addBrowserSuggest(input: HTMLInputElement) {
    if (input && this._defaultAutocompleteValueOfStreet) {
      input.setAttribute(
        'autocomplete',
        this._defaultAutocompleteValueOfStreet
      );
    }
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  applySuggest(suggest: InterfacePlacesList.Prediction) {
    this._orderFormFascade.config$
      .pipe(
        take(1),
        switchMap((config) =>
          this.getSuggestDetails$(suggest.place_id, this._sessionToken).pipe(
            map((place) => {
              return {
                place,
                config,
              };
            })
          )
        )
      )
      .subscribe((result) => {
        this.handleSuggestSelect(
          'buyer_address_street',
          'buyer_address_street_number',
          'buyer_address_zipcode',
          'buyer_address_city',
          'buyer_address_state',
          'buyer_address_country',
          result.config.hide_street_number,
          result.config.is_street_number_show_first,
          result.place,
          this.formGroupRef
        );
        /*
         * Die Sitzung endet bei Google, wenn die Details abgefragt wurden.
         * Deshalb wird an dieser Stelle ein neues Token erzeugt.
         */
        this._sessionToken = generateUUID();
      });
  }

  async handleSuggestSelect(
    streetId,
    streetNrId,
    plzId,
    cityId,
    stateId,
    countryId,
    is_street_number_hidden,
    is_street_number_before_street,
    place: InterfacePlaceDetail.Result,
    ngFormGroup: UntypedFormGroup
  ) {
    this._disableSuggestWhileSuggestSelection = true;
    /**
     * Default values & mappings
     */
    const componentForm = {
      street_number: 'short_name',
      route: 'long_name',
      locality: 'long_name',
      administrative_area_level_1: 'short_name',
      country: 'short_name',
      postal_code: 'short_name',
    };
    const elementsMap = {
      street_number: streetNrId,
      route: streetId,
      locality: cityId,
      administrative_area_level_1: stateId,
      country: countryId,
      postal_code: plzId,
    };
    const sanitizedValues = {
      street_number: '',
      route: '',
      locality: '',
      administrative_area_level_1: '',
      country: '',
      postal_code: '',
    };

    place.address_components.forEach((item) => {
      const addressType = item.types[0];
      if (componentForm[addressType]) {
        const val = item[componentForm[addressType]];
        if (typeof sanitizedValues[addressType] != 'undefined') {
          sanitizedValues[addressType] = val;
        }
      }
    });

    // if suggest without country found, do nothing
    if (!sanitizedValues.country) {
      return;
    }

    if (is_street_number_hidden) {
      const street_number = sanitizedValues.street_number;
      if (street_number) {
        sanitizedValues.route = is_street_number_before_street
          ? street_number + ' ' + sanitizedValues.route
          : sanitizedValues.route + ' ' + street_number;
      }
    }

    Object.keys(sanitizedValues).forEach((addressType) => {
      const val = sanitizedValues[addressType];
      const elementId = elementsMap[addressType];
      const control = ngFormGroup.get(elementId);
      if (elementId && control) {
        control.patchValue(val);
        control.markAsTouched();
      }
    });

    // sync data on autocomplete suggest success
    await firstValueFrom(this._orderFormFascade.update$());
    this._disableSuggestWhileSuggestSelection = false;
  }
}
